import { FadeWrapper } from 'components/authentication/styled';
import { InsightSummaryRenderer } from 'components/insight-summary-renderer';
import { insightTypeToIcon } from 'helpers/insight';
import { useIntersectionObserver } from 'hooks/useIntersectionObserver';
import React, { useRef, useState } from 'react';
import { WithContentSlotSidePaddingContainer } from 'ds/components/Layout';
import { H2, Text } from 'ds/components/typography';
import { FeedbackDropdown } from './FeedbackDropdown';
import { IHighlight } from './types';
import { truncate } from 'lodash';
import { headerHeightConsts } from 'ds/theme/default';
import {
  HighlightHeader,
  HighlightIconWrapper,
  HighlightHeaderTitle,
  HighlightWrapper,
  HighlightBody,
  HighlightTitle,
  HighlightFooter,
} from './styled';

interface HighlightProps extends IHighlight {
  isTransitioning: boolean;
}

const intersectionOptions = {
  threshold: [ 0, 1 ],
  rootMargin: `-${headerHeightConsts.desktop.autocomplete + headerHeightConsts.desktop.filters + headerHeightConsts.desktop.menu}px 0px 0px 0px`,
};

export const HighlightDesktop: React.FunctionComponent<HighlightProps> = ({ isTransitioning, ...highlight }) => {
  const [ isTitleScrolled, setIsTitleScrolled ] = useState(false);
  const [ isScrolled, setIsScrolled ] = useState(false);
  const titleRef = useRef(null);

  const onIntersect = ([ entry ]: IntersectionObserverEntry[]) => {
    if (entry.intersectionRatio === 1) {
      setIsScrolled(false);
      setIsTitleScrolled(false);
    }
    else if (entry.intersectionRatio === 0) {
      setIsScrolled(true);
      setIsTitleScrolled(true);
    }
    else {
      setIsScrolled(true);
    }
  };

  useIntersectionObserver(onIntersect, [ titleRef ], intersectionOptions);

  return (
    <>
      <HighlightHeader isScrolled={isScrolled && !isTransitioning} data-auto="desktop-opened-tradeoff-title">
        <HighlightIconWrapper insightType={highlight.type}>
          {insightTypeToIcon[highlight.type]}
        </HighlightIconWrapper>
        <FadeWrapper isVisible={isTitleScrolled} deferTime={100}>
          <HighlightHeaderTitle insightType={highlight.type}>
            <Text data-auto="desktop-opened-tradeoff-title-text">{truncate(highlight.previewText, { length: 50 })}</Text>
          </HighlightHeaderTitle>
        </FadeWrapper>
      </HighlightHeader>
      <HighlightWrapper data-auto="desktop-opened-tradeoff">
        <HighlightBody>
          <WithContentSlotSidePaddingContainer>
            <HighlightTitle insightType={highlight.type} ref={titleRef}>
              <H2>{highlight.title}</H2>
            </HighlightTitle>
          </WithContentSlotSidePaddingContainer>
          <WithContentSlotSidePaddingContainer>
            <Text>
              <InsightSummaryRenderer type={highlight.subType} summary={highlight.text} />
            </Text>
          </WithContentSlotSidePaddingContainer>
        </HighlightBody>
        <HighlightFooter>
          <WithContentSlotSidePaddingContainer>
            <FeedbackDropdown insightId={highlight.id} />
          </WithContentSlotSidePaddingContainer>
        </HighlightFooter>
      </HighlightWrapper>
    </>
  );
};

export default HighlightDesktop;
